import classNames from 'classnames';
import { memo, type PropsWithChildren } from 'react';

import { Switch, type SwitchProps } from '@air/primitive-switch';

export type ToggleFieldStatusText = {
  on: string;
  off: string;
};

export type ToggleFieldProps = Omit<SwitchProps, 'id'> & {
  id: string;
  label: string;
  dataTestId?: string;
  labelTestId?: string;
  statusText?: ToggleFieldStatusText;
  className?: string;
  labelClassName?: string;
  switchClassName?: string;
};

export const DEFAULT_TOGGLE_FIELD_STATUS_TEXT: ToggleFieldStatusText = {
  off: 'Off',
  on: 'On',
};

export const ToggleField = memo(
  ({
    checked,
    disabled,
    isLoading,
    onCheckedChange,
    id,
    label,
    dataTestId,
    labelTestId,
    statusText = DEFAULT_TOGGLE_FIELD_STATUS_TEXT,
    className,
    labelClassName,
    switchClassName,
    children,
    ...switchProps
  }: PropsWithChildren<ToggleFieldProps>) => (
    <div className={classNames('flex w-full items-center justify-between', className)}>
      <div className="mt-1 flex items-center">
        <label
          className={classNames('mr-2 text-16 text-grey-12', labelClassName)}
          data-testid={labelTestId}
          htmlFor={id}
        >
          {label}
        </label>
        {children}
      </div>
      <div className="flex flex-row items-center gap-2">
        {statusText && (
          <div className="font-medium text-12 text-grey-9 uppercase">{checked ? statusText.on : statusText.off}</div>
        )}
        <Switch
          checked={checked}
          className={switchClassName}
          data-id={id}
          data-testid={dataTestId}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          onCheckedChange={onCheckedChange}
          {...switchProps}
        />
      </div>
    </div>
  ),
);

ToggleField.displayName = 'ToggleField';
